import React from 'react';
import TrendChart from './TrendChart';

// Skip-words list shared with ItemRow's substitution matcher.
const SKIP_WORDS = new Set(['mix','sauce','fresh','choice','prime','fancy','bulk','grade','style',
  'seasoned','roasted','sliced','diced','shredded','whole','cut','frozen','iqf',
  'the','and','for','with','pre','raw','cooked','case','each','pack','bag',
  'heavy','duty','light','low','fat','free','all','purpose','natural',
  'extra','large','small','medium','size','count','gallon','quart','pint']);

function computeSubstitutes(item, foodItems, rangeDays) {
  if (!item.current_price || !item.category) return [];
  const currentPriceLb = item.price_per_lb ? Number(item.price_per_lb) : null;
  if (!currentPriceLb) return [];
  const cleanName = item.normalized_name.toLowerCase().replace(/\(.*\)/, '').trim();
  const foodWords = cleanName.split(/\s+/).filter(w => w.length > 2 && !SKIP_WORDS.has(w));
  if (foodWords.length === 0) return [];
  const mainProtein = foodWords[0];
  // Normalize spend (which now reflects the user-selected range) into a monthly basis so
  // "Save ~$X/mo" stays meaningful regardless of how long the range is.
  const monthlyBasis = item.totalSpend90d > 0 && rangeDays > 0 ? item.totalSpend90d * (30 / rangeDays) : 0;

  return foodItems
    .filter(other => {
      if (other.id === item.id) return false;
      if (other.category !== item.category) return false;
      const otherPriceLb = other.price_per_lb ? Number(other.price_per_lb) : null;
      if (!otherPriceLb || otherPriceLb >= currentPriceLb * 0.9) return false;
      const otherClean = other.normalized_name.toLowerCase().replace(/\(.*\)/, '').trim();
      const otherFoodWords = otherClean.split(/\s+/).filter(w => w.length > 2 && !SKIP_WORDS.has(w));
      const overlap = foodWords.filter(w => otherFoodWords.includes(w));
      if (overlap.length < 2) return false;
      if (mainProtein !== otherFoodWords[0]) return false;
      return true;
    })
    .map(other => {
      const otherPriceLb = Number(other.price_per_lb);
      const savings = currentPriceLb - otherPriceLb;
      const savingsPct = (savings / currentPriceLb) * 100;
      const monthlySavings = monthlyBasis > 0 ? (savingsPct / 100) * monthlyBasis : 0;
      return { ...other, otherPriceLb, savings, savingsPct, monthlySavings };
    })
    .sort((a, b) => b.savingsPct - a.savingsPct)
    .slice(0, 3);
}

// Shared detail body: trend chart + invoice history cards + edit override + substitutions + regional.
// Rendered in both the inline table-row drawer AND the detail modal.
export default function ItemDetail({
  item,
  priceHistory,
  regional,
  companyRegion,
  foodItems,
  rangeDays = 90,
  expandedHistoryEntries,
  onToggleHistoryEntry,
  editingHistoryEntry,
  editPriceValue, setEditPriceValue,
  editQtyValue, setEditQtyValue,
  editLineTotalValue, setEditLineTotalValue,
  onStartEditEntry,
  onCancelEditEntry,
  onSaveEditEntry,
}) {
  const substitutes = computeSubstitutes(item, foodItems, rangeDays);
  // Spend in the items table's "$ Spend / range" column reflects the user-selected range;
  // normalize it back to a 30-day rate for the "/mo" labels in the regional comparison panel.
  const monthlyBasis = item.totalSpend90d > 0 && rangeDays > 0 ? item.totalSpend90d * (30 / rangeDays) : 0;

  return (
    <>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h4 className="text-sm font-semibold text-gray-900">
          {item.normalized_name}
          {item.product_code && <span className="text-gray-400 font-normal ml-1" style={{ fontSize: 11 }}>#{item.product_code}</span>}
        </h4>
        <div className="flex items-center gap-4 text-xs text-gray-500 flex-wrap">
          {item.pack_size && <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-medium">{item.pack_size}{item.is_catch_weight ? ' (catch-weight)' : (item.weight_per_case ? ` (${item.weight_per_case} lb/cs)` : '')}</span>}
          {item.price_per_lb != null && <span>/lb: <strong className="text-gray-900">${Number(item.price_per_lb).toFixed(2)}</strong></span>}
          {item.totalSpend90d > 0 && <span>Spend / range: <strong className="text-gray-900">${item.totalSpend90d.toLocaleString('en-US', { maximumFractionDigits: 0 })}</strong></span>}
          {item.dollarImpact !== 0 && item.dollarImpact != null && <span className={item.dollarImpact > 0 ? 'text-red-600' : 'text-green-600'}><strong>{item.dollarImpact > 0 ? '+' : '-'}${Math.abs(item.dollarImpact).toFixed(0)}</strong></span>}
        </div>
      </div>
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <TrendChart data={priceHistory} />
      </div>
      {priceHistory && priceHistory.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-3">
          {priceHistory.map((p, i) => (
            <div key={i} className="text-xs bg-white border border-gray-200 rounded-lg px-3 py-2 cursor-pointer hover:border-gray-400 transition-colors"
              onClick={(e) => { e.stopPropagation(); onToggleHistoryEntry(`${item.id}-${i}`); }}>
              <div className="flex items-center gap-2">
                <span className="text-gray-400">{new Date(p.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' })}</span>
                <span className="font-semibold text-gray-900">${Number(p.price).toFixed(2)}/cs</span>
                {p.price_per_lb && <span className="text-gray-400">${Number(p.price_per_lb).toFixed(2)}/lb</span>}
                {i > 0 && (() => {
                  const prevEntry = priceHistory[i - 1];
                  const usePerLb = p.price_per_lb != null && prevEntry.price_per_lb != null;
                  const curr = usePerLb ? Number(p.price_per_lb) : Number(p.price);
                  const prev = usePerLb ? Number(prevEntry.price_per_lb) : Number(prevEntry.price);
                  if (!prev) return null;
                  const diff = ((curr - prev) / prev) * 100;
                  if (Math.abs(diff) < 0.1) return null;
                  return <span className={`font-medium ${diff > 0 ? 'text-red-500' : 'text-green-600'}`}>{diff > 0 ? '+' : ''}{diff.toFixed(1)}%</span>;
                })()}
              </div>
              {expandedHistoryEntries.has(`${item.id}-${i}`) && (
                <div className="mt-2 pt-2 border-t border-gray-100 text-xs text-gray-500 space-y-0.5">
                  <div>Invoice: {p.vendor || 'Unknown'} — {p.date}</div>
                  {p.case_price && <div>Case price: ${Number(p.case_price).toFixed(2)}</div>}
                  {p.price_per_lb && <div>Per lb: ${Number(p.price_per_lb).toFixed(2)}</div>}
                  {p.quantity && <div>Qty: {p.quantity} {p.unit || 'case'}</div>}
                  {p.line_total && <div>Line total: ${Number(p.line_total).toFixed(2)}</div>}
                  {p.pack_info && <div>Pack: {p.pack_info}</div>}
                  <div className="pt-1.5 mt-1.5 border-t border-gray-100" onClick={(e) => e.stopPropagation()}>
                    {editingHistoryEntry === `${item.id}-${i}` ? (
                      <div className="space-y-2">
                        <div className="text-gray-400 text-xs">Edit any one field — the others recompute. Edit two to override both.</div>
                        <div className="flex flex-wrap items-center gap-2">
                          <label className="flex items-center gap-1">
                            <span className="text-gray-400">Price/cs:</span>
                            <input
                              type="number" step="0.01" value={editPriceValue}
                              onChange={(e) => setEditPriceValue(e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              className="border border-gray-300 rounded px-2 py-1 text-xs w-24 focus:outline-none focus:border-gray-900"
                              autoFocus
                            />
                          </label>
                          <label className="flex items-center gap-1">
                            <span className="text-gray-400"># cs:</span>
                            <input
                              type="number" step="1" min="1" value={editQtyValue}
                              onChange={(e) => setEditQtyValue(e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              className="border border-gray-300 rounded px-2 py-1 text-xs w-16 focus:outline-none focus:border-gray-900"
                            />
                          </label>
                          <label className="flex items-center gap-1">
                            <span className="text-gray-400">Line total:</span>
                            <input
                              type="number" step="0.01" value={editLineTotalValue}
                              onChange={(e) => setEditLineTotalValue(e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              className="border border-gray-300 rounded px-2 py-1 text-xs w-24 focus:outline-none focus:border-gray-900"
                            />
                          </label>
                          <button
                            onClick={(e) => { e.stopPropagation(); onSaveEditEntry(p, i); }}
                            className="px-2 py-1 text-xs font-medium text-white rounded"
                            style={{ background: '#1a1a1a' }}
                          >
                            Save
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); onCancelEditEntry(); }}
                            className="px-2 py-1 text-xs font-medium text-gray-600 rounded border border-gray-300"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={(e) => { e.stopPropagation(); onStartEditEntry(p, i); }}
                        className="text-xs text-gray-500 hover:text-gray-900 underline"
                      >
                        Edit price / qty / total (override parse)
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {substitutes.length > 0 && (
        <div className="mt-3 bg-white rounded-xl border border-amber-100 p-3">
          <div className="flex items-center gap-2 mb-2">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#D97706" strokeWidth="2"><path d="M13 10V3L4 14h7v7l9-11h-7z"/></svg>
            <span className="text-xs font-semibold text-amber-700">Similar Items You Buy for Less</span>
          </div>
          <div className="space-y-2">
            {substitutes.map((sub, si) => (
              <div key={si} className="flex items-center justify-between bg-amber-50/50 rounded-lg px-3 py-2">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-gray-900">
                    {sub.normalized_name}
                    {sub.product_code && <span className="text-gray-300 font-normal ml-1" style={{ fontSize: 9 }}>#{sub.product_code}</span>}
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    ${sub.otherPriceLb.toFixed(2)}/lb
                    <span className="text-amber-600 font-medium ml-1">— {sub.savingsPct.toFixed(0)}% less per lb</span>
                  </div>
                </div>
                {sub.monthlySavings > 0 && (
                  <span className="text-xs font-bold text-green-600 flex-shrink-0 ml-2">
                    Save ~${sub.monthlySavings.toFixed(0)}/mo
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {regional && (regional.isActive ? (
        <div className={`mt-3 bg-white rounded-xl border p-3 ${regional.diff > 2 ? 'border-red-200' : 'border-blue-100'}`}>
          <div className="flex items-center gap-2 mb-2">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={regional.diff > 2 ? '#DC2626' : '#3B82F6'} strokeWidth="2"><path d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/><path d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
            <span className={`text-xs font-semibold ${regional.diff > 2 ? 'text-red-700' : 'text-blue-700'}`}>{companyRegion} Regional Average</span>
            <span className="text-xs text-gray-400">({regional.contributors} restaurants)</span>
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            <div className="text-sm">
              <span className="text-gray-500">Regional avg: </span>
              <span className="font-bold text-gray-900">${regional.avg.toFixed(2)}</span>
            </div>
            <div className="text-sm">
              <span className="text-gray-500">You pay: </span>
              <span className={`font-bold ${regional.diff > 2 ? 'text-red-600' : 'text-gray-900'}`}>${regional.yourPrice?.toFixed(2) || '--'}</span>
            </div>
            {regional.diff != null && (
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${regional.diff > 2 ? 'bg-red-50 text-red-600' : regional.diff < -2 ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-500'}`}>
                {regional.diff > 0 ? '+' : ''}{regional.diff.toFixed(1)}% vs avg
              </span>
            )}
          </div>
          {regional.diff > 2 && regional.yourPrice && item.totalSpend90d > 0 && (
            <div className="mt-3 pt-2.5 border-t border-red-100 flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2.5"><path d="M12 9v4m0 4h.01M12 2a10 10 0 100 20 10 10 0 000-20z"/></svg>
              </div>
              <div className="text-sm">
                <span className="text-gray-600">Overpaying has cost you an estimated </span>
                <span className="font-bold text-red-600">${((regional.diff / 100) * item.totalSpend90d).toFixed(2)}</span>
                <span className="text-gray-600"> over the selected range</span>
                {monthlyBasis > 0 && (
                  <span className="text-gray-400 text-xs ml-1">(${((regional.diff / 100) * monthlyBasis).toFixed(0)}/mo)</span>
                )}
              </div>
            </div>
          )}
          {regional.diff < -2 && regional.yourPrice && item.totalSpend90d > 0 && (
            <div className="mt-3 pt-2.5 border-t border-green-100 flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#2D8F5E" strokeWidth="2.5"><path d="M5 13l4 4L19 7"/></svg>
              </div>
              <div className="text-sm">
                <span className="text-gray-600">Great price — saving an estimated </span>
                <span className="font-bold text-green-600">${((Math.abs(regional.diff) / 100) * item.totalSpend90d).toFixed(2)}</span>
                <span className="text-gray-600"> vs regional avg over the selected range</span>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="mt-3 bg-white rounded-xl border border-dashed border-gray-200 p-3">
          <div className="flex items-center gap-2">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2"><path d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/><path d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
            <span className="text-xs text-gray-400">Regional comparison — collecting data ({regional.contributors}/3 restaurants contributing)</span>
          </div>
          <div className="mt-1.5 w-full bg-gray-100 rounded-full h-1.5">
            <div className="bg-blue-400 h-1.5 rounded-full transition-all" style={{ width: `${Math.min(100, (regional.contributors / 3) * 100)}%` }} />
          </div>
        </div>
      ))}
    </>
  );
}
